import {Inject, Injectable} from '@angular/core';
import {firstValueFrom, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Job, JOB_TYPE, JOB_TYPES, JobGroup, JobGroupState, RtJobState} from 'src/app/models/Job';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {AngularFireDatabase} from '@angular/fire/compat/database';
import {RtdbService} from 'src/app/services/rtdb.service';
import {FirebaseApp} from '@angular/fire/app';
import {getFunctions, httpsCallable} from '@angular/fire/functions';

@Injectable({
  providedIn: 'root'
})
export class JobService {
  private deleteJobGroupFn: (
    data: { jobGroupId: string }
  ) => Promise<{ data: { success: boolean } }>;

  constructor(
    @Inject(FirebaseApp) private app: FirebaseApp,
    private afs: AngularFirestore,
    private rtdb: AngularFireDatabase,
    private rtdbservice: RtdbService
  ) {
    const functions = getFunctions(this.app, 'europe-west1');
    this.deleteJobGroupFn = httpsCallable<
      { jobGroupId: string },
      { success: boolean }
    >(functions, 'deleteJobGroup');
  }

  getJobsForProject(projectId: string): Observable<Job[]> {
    return this.afs.collection('jobs', (ref) => {
      return ref
        .where('projectId', '==', projectId)
        .where('type', '==', JOB_TYPES.JOB);
    }).valueChanges() as Observable<Job[]>;
  }

  getJobGroupsForProject(projectId: string): Observable<JobGroup[]> {
    return this.afs.collection('jobs', (ref) => {
      return ref
        .where('projectId', '==', projectId)
        .where('type', '==', JOB_TYPES.JOB_GROUP);
    }).valueChanges() as Observable<JobGroup[]>;
  }

  getPdfRegenerationJobGroupsForProject(projectId: string): Observable<JobGroup[]> {
    return this.getJobGroupsForProject(projectId).pipe(
      map(jobGroups => jobGroups
        .filter(jobGroup => jobGroup.jobType === JOB_TYPE.PDF_REGENERATION)
        .sort((a, b) => {
          const aDate = (a.createdAt as any)?.toDate ? (a.createdAt as any).toDate().getTime() : 0;
          const bDate = (b.createdAt as any)?.toDate ? (b.createdAt as any).toDate().getTime() : 0;
          return bDate - aDate;
        }))
    );
  }

  // One-shot fetch (bypasses live subscription cache). Use when the live listener may be stale.
  async getLatestPdfRegenerationJobGroupOnce(projectId: string): Promise<JobGroup | undefined> {
    const snapshot = await firstValueFrom(
      this.afs.collection('jobs', (ref) => ref
        .where('projectId', '==', projectId)
        .where('type', '==', JOB_TYPES.JOB_GROUP)
      ).get()
    );
    const groups = snapshot.docs
      .map(d => d.data() as JobGroup)
      .filter(g => g.jobType === JOB_TYPE.PDF_REGENERATION)
      .sort((a, b) => {
        const aDate = (a.createdAt as any)?.toDate ? (a.createdAt as any).toDate().getTime() : 0;
        const bDate = (b.createdAt as any)?.toDate ? (b.createdAt as any).toDate().getTime() : 0;
        return bDate - aDate;
      });
    return groups[0];
  }

  getJobsForJobGroup(jobGroupId: string): Observable<Job[]> {
    return this.afs.collection('jobs', (ref) => {
      return ref.where('jobGroupId', '==', jobGroupId);
    }).valueChanges() as Observable<Job[]>;
  }

  getJobGroupStatesForProject(projectId: string): Observable<JobGroupState[]> {
    return this.afs.collection('jobs', (ref) => {
      return ref
        .where('projectId', '==', projectId)
        .where('type', '==', JOB_TYPES.JOB_GROUP_STATE);
    }).valueChanges() as Observable<JobGroupState[]>;
  }

  rtDbGetJobStatesForProject(
    projectId: string,
    jobPath = 'objectImport'
  ): Observable<(RtJobState | Partial<JobGroup>)[]> {
    const rtdbProjectId = this.rtdbservice.encodeRtdbKey(projectId);
    const jobsRef = this.rtdb.database.ref(`jobs/${rtdbProjectId}/${jobPath}`);

    return new Observable<any[]>(observer => {
      const listener = jobsRef.on(
        'value',
        snapshot => {
          const data = snapshot.val();
          const array = data && typeof data === 'object'
            ? Object.entries(data).map(([id, value]) => ({
              id,
              ...(typeof value === 'object' && value !== null ? value : {})
            }))
            : [];
          observer.next(array);
        },
        error => {
          console.error('RTDB read error:', error);
          observer.error(error);
        }
      );

      // Cleanup on unsubscribe
      return () => jobsRef.off('value', listener);
    });
  }

  async deleteJobGroup(jobGroupId: string): Promise<void> {
    await this.deleteJobGroupFn({jobGroupId});
  }

}
